import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'Bi-Vocational Missionary Support Raising: Combining a Job With Donor Support';
const DESCRIPTION =
    "A practical guide to raising missionary support alongside a job — how to size a realistic support goal when you have other income, and how to explain the arrangement to donors so it builds trust instead of raising questions.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover8.jpg',
            datePublished: '2026-07-28',
            dateModified: '2026-07-28',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/bi-vocational-missionary-support-raising',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'What does "bi-vocational missionary" mean?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'A bi-vocational (or "tentmaking") missionary earns income from a job, freelance work, or business alongside — not instead of — donor support, using that income to cover part of their living costs while raising support for the rest, or for ministry-specific expenses their job income does not cover.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Do I still need to raise full support if I have a job?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "It depends on your role and sending organization's model. Some bi-vocational missionaries raise a smaller support goal to cover the gap between job income and total field costs; others raise support for ministry expenses (travel, programs, housing subsidies) entirely separate from personal living costs covered by their job. Get clear on which model you're using before you set a number.",
                    },
                },
                {
                    '@type': 'Question',
                    name: "Will having a job make donors think I don't need their support?",
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "It can, if you don't explain the arrangement clearly. Donors who don't understand why a working missionary still needs support may assume the job covers everything. State plainly what your job income covers, what it doesn't, and why their gift still matters — specificity removes the doubt.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Can I use a support-raising platform if I only need partial support?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes — a recurring giving page works the same whether your goal is full field support or a smaller supplemental amount. Set the goal to the actual gap you're raising for, not a generic full-support figure, so donors can see their gift is meaningfully closing a real, specific need.",
                    },
                },
            ],
        },
    ],
};

export default function BiVocationalMissionarySupportRaising() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/bi-vocational-missionary-support-raising' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-bi-vocational-support-raising-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover8.jpg" alt="A missionary working at a laptop in a small workshop, representing bi-vocational or tentmaking ministry alongside a job" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Bi-vocational — or "tentmaking" — missionaries work a job or run a business while also serving on the field, and that arrangement raises a question full-support missionaries rarely face: if you have income, why are you still asking for donor support? The honest answer is usually that a job covers part of the picture, not all of it, but that nuance gets lost unless you explain it clearly. Done well, combining a job with donor support isn't a compromise — it's a legitimate, increasingly common model that can actually build more donor trust, because you're visibly sharing the financial load rather than asking others to carry all of it.</p>

                    <h2>Get clear on which model you're actually using</h2>

                    <p>"Bi-vocational" covers a range of arrangements, and donors will have very different mental models unless you specify which one applies to you. Some missionaries use job income to cover personal living costs entirely and raise support only for ministry-specific expenses like travel, programs, or translation work. Others use job income to close part of a support gap, raising the remainder from donors the same way a full-support missionary would. Decide which structure you're actually running before you write a single word of your appeal — a vague blend of the two is the version that confuses donors most.</p>

                    <h2>Size your ask around the real gap, not a generic full-support number</h2>

                    <p>Don't default to the standard full-support figure you'd see in a typical field budget if a chunk of it is already covered by job income. Build your actual number the way you would any other missionary budget — see <a href="/resources/how-much-support-does-a-missionary-need">how much support does a missionary need</a> for the category breakdown — then subtract what your job realistically covers. Asking for a support goal that matches your real, specific gap is both more honest and more compelling than an inflated number donors might suspect isn't accurate.</p>

                    <h2>Explain the arrangement before donors have to ask</h2>

                    <p>The single biggest risk in bi-vocational fundraising is silence about the job. If donors learn about your income only after they've committed, some will feel misled even if nothing was hidden on purpose — and others will quietly assume you don't need their gift and decline before they even ask a clarifying question. State plainly, in your first support letter or page, what your job covers, what it doesn't, and why the remaining gap still matters. Specificity here does more to build trust than omission ever will.</p>

                    <h2>Frame the job as commitment, not a fallback</h2>

                    <p>Some missionaries worry that mentioning outside income makes them look less "all in" than a full-support peer. In practice, most donors respond well to a bi-vocational model once it's explained — it signals financial responsibility and reduces their sense that they're carrying the entire weight of your field costs alone. Position the job as part of your strategy for sustainable, long-term ministry, not as a compromise you're apologizing for.</p>

                    <h2>Keep the ministry ask and the job separate in your messaging</h2>

                    <p>Avoid blending updates about your job with updates about ministry impact in a way that makes it unclear what donor gifts are actually funding. Structure your prayer letters and page updates so the ministry section stands on its own — what donor support specifically enabled — even if a separate line acknowledges the job that covers the rest. Donors give to the mission, not the job, and the clearer that distinction stays, the easier it is for them to see their gift's actual impact.</p>

                    <h2>Set up recurring giving for the gap, not a one-time push</h2>

                    <p>Because a bi-vocational support gap is often smaller and more stable than a full-support budget, it's usually a good fit for recurring monthly gifts rather than a single large campaign. A smaller, clearly explained monthly ask ("$150/month covers what my job income doesn't") tends to convert better than a large lump-sum appeal, and it gives donors an easy, low-friction way to become an ongoing part of a specific, well-understood need.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>What does "bi-vocational missionary" mean?</strong><br />
                    Someone who earns income from a job or business alongside donor support, rather than relying on donor support alone.</p>

                    <p><strong>Do I still need to raise full support if I have a job?</strong><br />
                    Usually not — most bi-vocational missionaries raise support for the specific gap between job income and total field or ministry costs.</p>

                    <p><strong>Will having a job make donors think I don't need their support?</strong><br />
                    It can, unless you explain clearly what the job covers, what it doesn't, and why the gap still matters.</p>

                    <p><strong>Can I use a support-raising platform if I only need partial support?</strong><br />
                    Yes — set your goal to the actual gap you're raising for so donors see their gift closing a specific, real need.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/how-much-support-does-a-missionary-need">How Much Support Does a Missionary Need? A Realistic Monthly Budget Breakdown</a></li>
                        <li><a href="/resources/how-to-write-a-missionary-support-letter">How to Write a Missionary Support Letter That Actually Gets You Funded</a></li>
                        <li><a href="/resources/raise-support-fast-30-60-90-day-plan">How to Raise Support Fast: A 30/60/90-Day Plan for New Missionaries</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We built this after hearing from bi-vocational missionaries who struggled to explain their support model clearly, and lost donors to confusion rather than an actual no.</p>
                    <p><small><em>Last updated: 2026-07-28.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/bi-vocational-missionary-support-raising" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
